import React, { useState } from 'react';
import {
  Box,
  Typography,
  Chip,
  Alert,
  CircularProgress,
  useTheme,
  Paper,
  Button,
} from '@mui/material';
import { Plus } from 'lucide-react';
import { ServiceAddonMapping } from '../../types/service';
import Notifications from '../common/Notifications';
import { NotificationT, SEVERITY } from '../../types/notification';
import { useServiceAddonMapping } from '../../hooks/service/useServiceAddon';
import { useServices } from '../../hooks/service/useServices';
import ServiceSelect from '../bookings/ServiceSelect';

interface ServiceAddonMappingSectionProps {
  serviceId: number;
  serviceAddonId?: number;
  existingMappings: ServiceAddonMapping[];
  onMappingsChange?: () => void;
}

const ServiceAddonMappingSection: React.FC<ServiceAddonMappingSectionProps> = ({
  serviceId,
  existingMappings,
  onMappingsChange,
}) => {
  const theme = useTheme();
  const [selectedAddonServices, setSelectedAddonServices] = useState<string[]>([]);
  const { createAddonMapping, deleteAddonMapping, isLoading, error } = useServiceAddonMapping();
  const { services } = useServices();
  const [localExistingMappings, setLocalExistingMappings] = useState(existingMappings);
  const [notification, setNotification] = useState<NotificationT>({
    open: false,
    message: '',
    severity: SEVERITY.SUCCESS,
  });

  // Filter out already mapped resources
  const mappedServiceAddonIds = existingMappings.map(m => m.serviceAddon.id.toString());
  const availableAddonServices = services.filter(
    r => !mappedServiceAddonIds.includes(r.id.toString()) && r.isAddonService
  );

  const handleAddServiceAddon = async () => {
    if (selectedAddonServices.length === 0) return;

    try {
      for (const serviceAddonId of selectedAddonServices) {
        await createAddonMapping(serviceId, parseInt(serviceAddonId, 10));
      }
      setNotification({
        open: true,
        message: 'Service addon mapped successfully',
        severity: SEVERITY.SUCCESS,
      });
      setSelectedAddonServices([]);
      onMappingsChange?.();
    } catch (error) {
      setNotification({
        open: true,
        message: 'Failed to map addon service',
        severity: SEVERITY.ERROR,
      });
    }
  };

  const handleRemoveResource = async (mappingId: number) => {
    try {
      await deleteAddonMapping(mappingId);
      setLocalExistingMappings(prevMappings =>
        prevMappings.filter(mapping => mapping.id !== mappingId)
      );
      setNotification({
        open: true,
        message: 'Service addon mapping removed successfully',
        severity: SEVERITY.SUCCESS,
      });
      onMappingsChange?.();
    } catch (error) {
      setNotification({
        open: true,
        message: 'Failed to remove service addon mapping',
        severity: SEVERITY.ERROR,
      });
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 3,
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 2,
        }}
      >
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
          Add Service Addon
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Box sx={{ flex: 1 }}>
            <ServiceSelect
              selectedService={selectedAddonServices}
              onChange={setSelectedAddonServices}
              availableService={availableAddonServices}
            />
          </Box>
          <Button
            variant="contained"
            disableElevation
            startIcon={<Plus size={16} />}
            onClick={handleAddServiceAddon}
            disabled={isLoading || selectedAddonServices.length === 0}
            sx={{
              height: 40,
              minWidth: 120,
            }}
          >
            {isLoading ? <CircularProgress size={20} /> : 'Add'}
          </Button>
        </Box>
      </Paper>

      <Paper
        elevation={0}
        sx={{
          p: 3,
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 2,
        }}
      >
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
          Mapped Addons
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {localExistingMappings.map(({ id, serviceAddon }) => (
            <Chip
              key={id}
              label={serviceAddon.name}
              onDelete={() => handleRemoveResource(id)}
              disabled={isLoading}
              sx={{
                backgroundColor: `${theme.palette.primary.main}15`,
                color: theme.palette.primary.main,
                '&:hover': {
                  backgroundColor: `${theme.palette.primary.main}25`,
                },
              }}
            />
          ))}
          {localExistingMappings.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              No resources mapped yet
            </Typography>
          )}
        </Box>
      </Paper>

      <Notifications
        open={notification.open}
        message={notification.message}
        severity={notification.severity}
        onClose={() => setNotification(prev => ({ ...prev, open: false }))}
      />
    </Box>
  );
};

export default ServiceAddonMappingSection;
